import { reactMount } from '@/lib/reactMount'
import { useRef } from 'react'
import { ArrowRightCircle, Facebook, Instagram, LinkedIn } from '@/components/Icons'
import { Button } from '@/components/Button'
import { BrandLogo } from '@/components/Logos'

const Link = ({
  href,
  children,
  className = '',
  target = null,
  onClick,
  ...props
}) => {
  const baseClasses = 'text-xs/5 text-default-50 hover:text-default-400 font-semibold'
  const combinedClasses = `${baseClasses} ${className}`.trim()

  return (
    <a
      href={href}
      className={combinedClasses}
      target={target}
      onClick={onClick}
      {...props}
    >
      {children}
    </a>
  )
}

const footer = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'dark'

  return (
    <footer data-theme={theme} className='relative isolate py-20 sm:py-24 md:px-4 lg:px-10 xl:px-24 bg-primary-950 overflow-hidden'>
      <div className='mx-auto max-w-[1260px] px-4 lg:px-6 text-xs/5 text-default-50 flex flex-col xl:flex-row xl:flex-wrap xl:justify-between'>

        <div className={`flex flex-col pb-10 xl:pb-0 sm:flex-row sm:justify-between xl:justify-start gap-6 xl:flex-col ${dataset.brandedDealerVersion === 'true' ? ' md:w-[620px] xl:w-6/12 max-sm:pb-4' : ' xl:w-3/12'}`}>
          <div data-theme='dark' className={`_site-header w-full  xl:w-full space-y-2 ${dataset.brandedDealerVersion === 'true' ? 'sm:flex gap-6 items-end sm:w-full' : 'sm:w-5/12'}`}>
            <a href='/'>
              <span className='sr-only'>{umbraco.Title ? umbraco.Title : 'UDC Finance'}</span>
              <BrandLogo className='w-[218px] h-auto' />
            </a>
            {umbraco?.Description && (
              <p className='text-balance text-xs font-extrabold text-default-50'>
                {umbraco.Description}
              </p>
            )}
          </div>

          {dataset.brandedDealerVersion !== 'true' && (
            <div className=' w-full sm:w-4/12 xl:w-9/12 space-y-2'>
              <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>Login</h3>
              <Button href='https://nz.udclive.co.nz/external/login' target='_blank' size='sm' variant='primary' className='w-full'>
                <span className='block relative pr-5'>UDC Live <ArrowRightCircle className='absolute right-0 top-1/2 -mt-[7px]' /></span>
              </Button>
              <Button href='https://my.udc.co.nz/logon' target='_blank' size='sm' variant='secondary' className='w-full'>
                <span className='block relative pr-5'>MyUDC <ArrowRightCircle className='absolute right-0 top-1/2 -mt-[7px]' /></span>
              </Button>
            </div>
          )}
        </div>

        {dataset.brandedDealerVersion !== 'true' && (
          <div className='grid grid-cols-2 sm:grid-cols-4 gap-6 xl:w-8/12'>
            <div className='space-y-2'>
              {umbraco.Column1?.Title && (
                <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Column1.Title}</h3>
              )}
              <ul role='list' className='space-y-4'>
                {umbraco.Column1.Buttons.map((item) => (
                  <li key={item.Name}>
                    <Link href={item.Url} target={item.Target}>
                      {item.Name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className='space-y-2 sm:hidden pt-2'>
                {umbraco.Column3?.Title && (
                  <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Column3.Title}</h3>
                )}
                <ul role='list' className='space-y-4'>
                  {umbraco.Column3.Buttons.map((item) => (
                    <li key={item.Name}>
                      <Link href={item.Url} target={item.Target}>
                        {item.Name}
                      </Link>
                    </li>
                  ))}
                </ul>
                {umbraco.Social?.Title && (
                  <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Social.Title}</h3>
                )}
                {umbraco.Social?.Buttons?.length && (
                  <ul role='list' className='flex items-center gap-4'>
                    {umbraco.Social.Buttons.map((item) => {
                      let IconComponent = null
                      if (item.Name === 'Facebook') IconComponent = Facebook
                      if (item.Name === 'Instagram') IconComponent = Instagram
                      if (item.Name === 'LinkedIn') IconComponent = LinkedIn
                      return (
                        <li key={item.Name}>
                          <a className='flex items-center justify-center size-6 rounded-[4px] bg-default-50 hover:bg-default-400' href={item.Url} target={item.Target}>
                            <span className='sr-only'>{item.Name}</span>
                            {IconComponent ? <IconComponent className='text-default-900' /> : null}
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className='space-y-2 sm:col-span-2'>
              {umbraco.Column2?.Title && (
                <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Column2.Title}</h3>
              )}
              <ul role='list' className='space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4'>
                {umbraco.Column2.Buttons.map((item) => (
                  <li key={item.Name}>
                    <Link href={item.Url} target={item.Target}>
                      {item.Name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className='space-y-2 hidden sm:block'>
              {umbraco.Column3?.Title && (
                <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Column3.Title}</h3>
              )}
              <ul role='list' className='space-y-4'>
                {umbraco.Column3.Buttons.map((item) => (
                  <li key={item.Name}>
                    <Link href={item.Url} target={item.Target}>
                      {item.Name}
                    </Link>
                  </li>
                ))}
              </ul>
              {umbraco.Social?.Title && (
                <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Social.Title}</h3>
              )}
              {umbraco.Social?.Buttons?.length && (
                <ul role='list' className='flex items-center gap-4'>
                  {umbraco.Social.Buttons.map((item) => {
                    let IconComponent = null
                    if (item.Name === 'Facebook') IconComponent = Facebook
                    if (item.Name === 'Instagram') IconComponent = Instagram
                    if (item.Name === 'LinkedIn') IconComponent = LinkedIn
                    return (
                      <li key={item.Name}>
                        <a className='flex items-center justify-center size-6 rounded-[4px] bg-default-50 hover:bg-default-400' href={item.Url} target={item.Target}>
                          <span className='sr-only'>{item.Name}</span>
                          {IconComponent ? <IconComponent className='text-default-900' /> : null}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className='w-full mx-auto max-w-[1280px] flex flex-col-reverse xl:flex-row flex-wrap xl:justify-between gap-6 pt-16'>
          <div className=''>
            {umbraco.Bottom?.Title && (
              <h3 className='tracking-widest text-2xs/6 uppercase font-semibold text-default-400'>{umbraco.Bottom.Title}</h3>
            )}
            {umbraco.Bottom?.Description && (
              <p>{umbraco.Bottom.Description}</p>
            )}
          </div>
          {umbraco.Bottom?.Buttons?.length && (
            <ul role='list' className='flex flex-wrap gap-4 items-center max-2xl:-ml-2'>
              {umbraco.Bottom.Buttons.map((item) => (
                <li key={item.Name}>
                  <Link className='p-2 font-semibold' href={item.Url} target={item.Target}>
                    {item.Name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </footer>
  )
}

reactMount('footer', footer)
